import * as React from 'react';
import { Spin } from 'antd';
import debounce from 'lodash/debounce';
import DimensionMap, { type DimensionMapProps, DimensionMapRef } from './DimensionMap';
import { MapLoaderContainer } from './styled';

// Only need to load the map library once, so use a static variable to cache the load status
let GOOGLE_MAP_LOADED = false;

/**
 * DimensionMapLoader
 *
 * @description DimensionMapLoader is a wrapper of DimensionMap,
 * it will load the google map library before render DimensionMap.
 * And it defines `debouncedSetCacheProps` and `cacheProps`,
 * used to porxy the props change action to avoid unnecessary re-render,
 * because the props will change frequently in live mode.
 */
const DimensionMapLoader = React.forwardRef<DimensionMapRef, DimensionMapProps>((props, ref) => {
  const [loaded, setLoaded] = React.useState(GOOGLE_MAP_LOADED);
  const [cacheProps, setCacheProps] = React.useState(props);
  // Debounce the setCacheProps to avoid unnecessary re-render,
  // because the props change frequently in live mode.
  // This will greatly improve the performance of map when data changes.
  const debouncedSetCacheProps = React.useMemo(() => debounce(setCacheProps, 500), []);

  React.useEffect(() => {
    (async () => {
      try {
        await google.maps.importLibrary('maps');
        GOOGLE_MAP_LOADED = true;
        setLoaded(true);
      } catch {
        // do nothing
      }
    })();
  }, []);

  React.useEffect(() => {
    debouncedSetCacheProps(props);
  }, [props, debouncedSetCacheProps]);

  return loaded ? (
    <DimensionMap {...cacheProps} ref={ref} />
  ) : (
    <MapLoaderContainer>
      <Spin />
    </MapLoaderContainer>
  );
});

DimensionMapLoader.displayName = 'DimensionMapLoader';

export default React.memo(DimensionMapLoader);

export type { DimensionMapProps, DimensionMapRef };
